<script setup lang="ts">
import { Link, router, usePage } from '@inertiajs/vue3';
import { AlertTriangle, ArrowLeft, RotateCcw, Save, Tag, Trash2 } from '@lucide/vue';
import { computed, defineAsyncComponent, onMounted, provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue-sonner';

import { Button } from '@/components/ui/button/index';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card/index';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/index';

import { useCrudBlockRegistry } from '../composables/useCrudBlockRegistry';
import { useEditorContext } from '../composables/useCrudContext';
import { useCrudForm } from '../composables/useCrudForm';
import { useEditorRealTime, type NonConflictingUpdateInfo } from '../composables/useEditorRealTime';
import { getNestedValue } from '../composables/useFieldVisibility';
import { useKeyboardShortcuts, type ShortcutConfig } from '../composables/useKeyboardShortcuts';
import { useRecentHistory } from '../composables/useRecentHistory';
import { useTranslatable } from '../composables/useTranslatable';
import type { ActionConfig, EditorPageProps, SharedCrudData } from '../types';
import { edit, index } from '../wayfinder/routes';

const DANGER_ZONE_TAB = '__danger_zone__';

import ConflictDialog from './ConflictDialog.vue';
import CrudTagsEditor from './CrudTagsEditor.vue';
import DeleteConfirmDialog from './DeleteConfirmDialog.vue';
import ListerProvider from './ListerProvider.vue';

const TabConfigComponent = defineAsyncComponent(() => import('./ConfigBuilder/TabConfig.vue'));
const CrudLister = defineAsyncComponent(() => import('./CrudLister.vue'));

// `currentTab` is omitted: this component reads it from the injected editor
// context (`contextProps.currentTab`) and re-derives it as the `currentTab`
// computed below. Declaring it as a prop too collides with that computed
// (vue/no-dupe-keys). No props are passed to <CrudEditor /> anyway — the page
// (pages/Editor.vue) provides the full context via provideEditorContext().
defineProps<Partial<Omit<EditorPageProps, 'currentTab'>>>();
const contextProps = useEditorContext();

const { t } = useI18n();
const page = usePage();

const editor = useCrudForm(contextProps);
const { activeModel, activeConfig } = editor;
const { trField, trTab, trBlock, trValue, te, t: trT } = useTranslatable(activeConfig);

// Block registry — lets custom blocks hook into the CRUD save/reset lifecycle
const blockRegistry = useCrudBlockRegistry();

/** Combined dirty state: CRUD form fields OR any registered block */
const combinedIsDirty = computed(() => editor.isDirty.value || blockRegistry.hasAnyDirty.value);

/** Save all dirty blocks then the CRUD form */
async function handleSave(): Promise<void> {
    await blockRegistry.saveAll();
    if (editor.isDirty.value) {
        editor.submit(currentTab.value);
    }
}

/** Reset all blocks and the CRUD form */
function handleReset(): void {
    blockRegistry.resetAll();
    editor.reset();
}

/** Handle field update supporting dot notation (e.g., 'data.start_date') */
function handleFieldUpdate(key: string, value: unknown): void {
    editor.setFieldValue(key, value);
}

const isMounted = ref(false);
const deleteDialogOpen = ref(false);
const { trackItem } = useRecentHistory();

// Get current user ID for real-time conflict detection
const currentUserId = computed(() => (page.props.auth as any)?.user?.id ?? null);

// Track fields that were recently updated by other users (for visual highlighting)
// Map from field key to actor info
const highlightedFields = ref<Map<string, { actorName: string }>>(new Map());

// Provide the highlighted fields state to child components
provide('highlightedFields', highlightedFields);

/**
 * Highlight a field temporarily to indicate it was changed by another user.
 */
function highlightField(fieldKey: string, actorName: string, duration = 5000): void {
    highlightedFields.value.set(fieldKey, { actorName });
    // Force reactivity update
    highlightedFields.value = new Map(highlightedFields.value);

    setTimeout(() => {
        highlightedFields.value.delete(fieldKey);
        highlightedFields.value = new Map(highlightedFields.value);
    }, duration);
}

// Resolve model name - prefer contextProps.model (direct from page props) over computed activeModel
const resolvedModelName = contextProps.model ?? activeModel.value;

// Debug logging for real-time subscription
console.log('[CrudEditor] Setup running', {
    'contextProps.model': contextProps.model,
    'contextProps.id': contextProps.id,
    'activeModel.value': activeModel.value,
    resolvedModelName,
    'currentUserId.value': currentUserId.value,
});

// Real-time conflict detection (only for edit mode, not create)
const shouldEnableRealTime = !!(contextProps.id && resolvedModelName);
console.log('[CrudEditor] Real-time enabled:', shouldEnableRealTime);

/**
 * Handle non-conflicting updates from other users.
 * Highlights the changed fields with actor name and merges the changes.
 */
function handleNonConflictingUpdate(info: NonConflictingUpdateInfo): void {
    console.log('[CrudEditor] Non-conflicting update received:', info);

    // Merge the server data into the form
    editor.mergeServerData(info.data);

    // Get actor name for display
    const actorName = info.actorName || t('crud.realtime.another_user');

    // Highlight the changed fields with actor name
    for (const fieldKey of info.changedFields) {
        highlightField(fieldKey, actorName, 5000);
    }
}

const { hasConflict, conflictInfo, dismissConflict } = shouldEnableRealTime
    ? useEditorRealTime({
          modelName: resolvedModelName,
          recordId: contextProps.id!,
          currentUserId: currentUserId.value,
          getLocalDirtyFields: () => editor.getDirtyFields(),
          onDeleted: () => {
              toast.warning(t('crud.realtime.record_deleted'));
              router.visit(backRoute.value);
          },
          onNonConflictingUpdate: handleNonConflictingUpdate,
      })
    : { hasConflict: ref(false), conflictInfo: ref(null), dismissConflict: () => {}, acceptServerChanges: () => null };

/** Accept server changes and merge them into the form (overwrites local changes) */
function handleAcceptServerChanges(serverData: Record<string, unknown>): void {
    console.log('[CrudEditor] handleAcceptServerChanges called with:', serverData);
    if (serverData && Object.keys(serverData).length > 0) {
        console.log('[CrudEditor] Calling editor.acceptServerData');
        // Use acceptServerData to overwrite even dirty fields
        editor.acceptServerData(serverData);
        console.log('[CrudEditor] acceptServerData completed');
    } else {
        console.log('[CrudEditor] serverData was empty');
    }
    // Dismiss conflict after accepting (in case update:open didn't trigger dismissConflict yet)
    dismissConflict();
}

onMounted(() => {
    isMounted.value = true;

    // Track this record in recent history if enabled
    const recentHistoryConfig = (activeConfig.value as any)?.recentHistory;
    if (recentHistoryConfig?.enabled !== false && contextProps.id && contextProps.item) {
        trackItem(activeModel.value, String(contextProps.id));
    }
});

/** Titles shortcut */
const titles = computed(() => {
    const prefix = activeConfig.value?.translationPrefix;
    const fallbackSingular = activeConfig.value?.titles.singular ?? '';
    const fallbackPlural = activeConfig.value?.titles.plural ?? '';
    
    if (!prefix) return { singular: fallbackSingular, plural: fallbackPlural };
    
    return {
        singular: te(`models.${prefix}.singular`) ? trT(`models.${prefix}.singular`) : fallbackSingular,
        plural: te(`models.${prefix}.plural`) ? trT(`models.${prefix}.plural`) : fallbackPlural,
    };
});

/** Editor config shortcut */
const editorConfig = computed(() => activeConfig.value?.editor);

/** First tab key for default */
const firstTab = computed(() => editorConfig.value?.tabs[0]?.key ?? '');

/** Current tab from props (comes from ?tab= query param) */
const currentTab = computed(() => contextProps.currentTab ?? firstTab.value);

/** Check if a tab is a child tab */
const isChildTab = (key: string) => {
    return editorConfig.value?.childTabs?.some((t) => t.key === key) ?? false;
};

/** Check if user has delete permission */
const crud = computed(() => (page.props as { crud?: SharedCrudData }).crud);
const canDelete = computed(() => crud.value?.active?.permissions?.delete === true);
const canManageTags = computed(() => crud.value?.active?.permissions?.update === true);

/** Check if current tab is the danger zone tab */
const isDangerZoneTab = computed(() => currentTab.value === DANGER_ZONE_TAB);
const tagsSheetOpen = ref(false);

/** Whether the tab bar should be visible (more than 1 tab total, or danger zone is available) */
const showTabBar = computed(() => {
    const regularTabs = editorConfig.value?.tabs.length ?? 0;
    const childTabs = editorConfig.value?.childTabs?.length ?? 0;
    const hasDangerZone = canDelete.value ? 1 : 0;
    return (regularTabs + childTabs + hasDangerZone) > 1 || canManageTags.value;
});

/** Navigate to a tab */
const navigateToTab = (tabKey: string) => {
    const baseUrl = edit({
        model: activeModel.value,
        id: String(contextProps.id),
    }).url;
    // Only add ?tab= if it's not the first regular tab
    if (tabKey === firstTab.value) {
        router.visit(baseUrl, { preserveScroll: true });
    } else {
        router.visit(`${baseUrl}?tab=${tabKey}`, { preserveScroll: true });
    }
};

/** Back route - preserves parent context */
const backRoute = computed(() => {
    const baseUrl = index({ model: activeModel.value }).url;
    if (contextProps.parentContext) {
        const params = new URLSearchParams({
            [contextProps.parentContext.foreignKey]: contextProps.parentContext.id,
        });
        return `${baseUrl}?${params.toString()}`;
    }
    return baseUrl;
});


/** Get subtitle value from subtitleFields, headerFields, or titleField */
const subtitleValue = computed(() => {
    if (!contextProps.item) return null;
    
    const item = contextProps.item as Record<string, unknown>;
    
    // Helper to get field value with fallbacks (resolves enum values through trValue)
    const getFieldValue = (fieldKey: string): string | null => {
        // Try getNestedValue first (handles camelCase/snake_case conversion and dot notation)
        let value = getNestedValue(item, fieldKey);

        // If undefined, try direct access
        if (value === undefined || value === null) {
            value = item[fieldKey];
        }

        if (value !== null && value !== undefined && value !== '') {
            const raw = String(value);
            return trValue(fieldKey, value as string | number | boolean, raw);
        }

        return null;
    };
    
    // Helper to construct subtitle from array of fields
    const constructSubtitle = (fields: string[]): string | null => {
        const values: string[] = [];
        for (const fieldKey of fields) {
            const value = getFieldValue(fieldKey);
            if (value) {
                values.push(value);
            }
        }
        return values.length > 0 ? values.join(' ') : null;
    };
    
    // Try subtitleFields first (can be array or single field)
    // Handle both camelCase (subtitleFields) and snake_case (subtitle_field) for compatibility
    const subtitleFieldsConfig = editorConfig.value?.subtitleFields ?? (editorConfig.value as any)?.subtitle_field;
    if (subtitleFieldsConfig) {
        if (Array.isArray(subtitleFieldsConfig)) {
            const value = constructSubtitle(subtitleFieldsConfig);
            if (value) return value;
        } else {
            const value = getFieldValue(subtitleFieldsConfig);
            if (value) return value;
        }
    }
    
    // Fallback to headerFields
    if (editorConfig.value?.headerFields && editorConfig.value.headerFields.length > 0) {
        const value = getFieldValue(editorConfig.value.headerFields[0]);
        if (value) return value;
    }
    
    // Fallback to titleField
    if (editorConfig.value?.titleField) {
        const value = getFieldValue(editorConfig.value.titleField);
        if (value) return value;
    }
    
    return null;
});

/** Editor actions from config - includes tab shortcuts */
const editorActions = computed(() => {
    // Get actions from editor config
    const configActions = editorConfig.value?.actions ?? [];
    return configActions as Array<ActionConfig & { shortcut?: ShortcutConfig }>;
});

/** Handle keyboard shortcuts for editor actions */
useKeyboardShortcuts(
    editorActions,
    (action) => {
        // Handle tab navigation actions (auto-generated with hidden flag)
        if (action.type === 'tab' && action.tabTarget) {
            navigateToTab(action.tabTarget);
            return;
        }

        // Handle event actions
        if (action.type === 'event' && action.eventName) {
            console.log('[CrudEditor] Dispatching event:', action.eventName, { id: contextProps.id, model: activeModel.value });
            window.dispatchEvent(
                new CustomEvent(action.eventName, {
                    detail: { id: contextProps.id, model: activeModel.value, payload: action.eventPayload },
                }),
            );
            return;
        }

        // Handle custom routes
        if (action.route) {
            const url = action.route.replace(':id', String(contextProps.id));
            const method = (action.method?.toLowerCase() ?? 'post') as any;

            if (method === 'get') {
                router.get(url, {}, { preserveScroll: true });
            } else {
                router[method as 'post' | 'put' | 'patch' | 'delete'](url, {}, {
                    preserveScroll: true,
                });
            }
            return;
        }

        // Handle other action types as needed
    },
    { skipInputs: true },
);
</script>

<template>
    <div v-if="activeConfig && editorConfig" class="-mb-6 space-y-6">
        <!-- Header -->
        <Teleport v-if="isMounted && !isChildTab(currentTab)" to="#header-portal">
            <div class="flex items-center gap-2 overflow-hidden px-2">
                <Link :href="backRoute">
                    <Button variant="ghost" size="icon-sm">
                        <ArrowLeft class="size-4" />
                    </Button>
                </Link>
                <div class="min-w-0">
                    <h1 class="truncate text-lg font-bold tracking-tight">
                        {{ t('crud.edit_title', { singular: titles.singular }) }}
                    </h1>
                    <p class="text-muted-foreground truncate text-xs">
                        {{ subtitleValue || t('crud.id', { id: contextProps.id }) }}
                    </p>
                </div>
            </div>
        </Teleport>

        <!-- Form -->
        <form id="crud-editor-form" @submit.prevent="handleSave">
            <Tabs :model-value="currentTab" class="space-y-4">
                <!-- Tab List -->
                <TabsList v-if="showTabBar">
                    <!-- Regular Tabs - use router navigation for all tabs -->
                    <TabsTrigger v-for="tab in editorConfig.tabs" :key="tab.key" :value="tab.key" @click.prevent="navigateToTab(tab.key)">
                        {{ trTab(tab.key) }}
                    </TabsTrigger>
                    <!-- Child Tabs - navigate with URL -->
                    <TabsTrigger
                        v-for="childTab in editorConfig.childTabs"
                        :key="childTab.key"
                        :value="childTab.key"
                        @click.prevent="navigateToTab(childTab.key)"
                    >
                        {{ trTab(childTab.key) }}
                    </TabsTrigger>
                    <!-- Tag icon button - opens tags sheet -->
                    <button
                        v-if="canManageTags"
                        type="button"
                        class="text-muted-foreground hover:text-foreground data-[active=true]:text-foreground inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors"
                        :data-active="tagsSheetOpen"
                        @click="tagsSheetOpen = true"
                    >
                        <Tag class="size-4" />
                    </button>
                    <!-- Danger Zone Tab - auto-added when user can delete -->
                    <TabsTrigger
                        v-if="canDelete"
                        :value="DANGER_ZONE_TAB"
                        class="text-destructive data-[state=active]:text-destructive"
                        @click.prevent="navigateToTab(DANGER_ZONE_TAB)"
                    >
                        {{ t('crud.danger_zone') }}
                    </TabsTrigger>
                </TabsList>

                <!-- Regular Tab Contents - only show if current tab is a regular tab -->
                <template v-if="!isChildTab(currentTab) && !isDangerZoneTab">
                    <TabsContent v-for="tab in editorConfig.tabs" :key="tab.key" :value="tab.key">
                        <TabConfigComponent
                            :tab="tab"
                            :form="editor.form"
                            :errors="editor.form.errors"
                            :tr-field="trField"
                            :tr-tab="trTab"
                            :tr-block="trBlock"
                            :translation-prefix="activeConfig?.translationPrefix"
                            :item="contextProps.item as Record<string, unknown>"
                            :model="activeModel"
                            :item-id="contextProps.id"
                            @update:field="handleFieldUpdate"
                        />
                    </TabsContent>
                </template>

                <!-- Child Tab Contents - only show if current tab is a child tab -->
                <template v-else-if="isChildTab(currentTab)">
                    <TabsContent v-for="childTab in editorConfig.childTabs" :key="childTab.key" :value="childTab.key">
                        <ListerProvider v-if="contextProps.childrenData?.[childTab.key]" :value="contextProps.childrenData[childTab.key] as any">
                            <CrudLister />
                        </ListerProvider>
                        <div v-else class="rounded-md border border-dashed p-8 text-center">
                            <p class="text-muted-foreground">{{ t('crud.no_data') }}</p>
                        </div>
                    </TabsContent>
                </template>

                <!-- Danger Zone Tab Content -->
                <TabsContent v-else-if="isDangerZoneTab" :value="DANGER_ZONE_TAB">
                    <Card class="border-destructive/50">
                        <CardHeader>
                            <div class="flex items-center gap-2">
                                <AlertTriangle class="text-destructive size-5" />
                                <CardTitle class="text-destructive">{{ t('crud.danger_zone') }}</CardTitle>
                            </div>
                            <CardDescription>
                                {{ t('crud.danger_zone_description', { singular: titles.singular.toLowerCase() }) }}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div class="flex items-center justify-between rounded-lg border p-4">
                                <div>
                                    <p class="font-medium">{{ t('crud.delete_confirm_title', { singular: titles.singular }) }}</p>
                                    <p class="text-muted-foreground text-sm">
                                        {{ t('crud.delete_confirm_description', { singular: titles.singular.toLowerCase() }) }}
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    @click="deleteDialogOpen = true"
                                >
                                    <Trash2 class="mr-2 size-4" />
                                    {{ t('crud.delete') }}
                                </Button>
                                <DeleteConfirmDialog
                                    :open="deleteDialogOpen"
                                    :singular="titles.singular"
                                    @update:open="deleteDialogOpen = $event"
                                    @confirm="editor.destroy"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <!-- Tags Sheet (opened via tag icon in tab bar) -->
            <CrudTagsEditor
                v-if="contextProps.id && activeModel && canManageTags"
                v-model:open="tagsSheetOpen"
                :model="activeModel"
                :model-value="(editor.getFieldValue('__crud_tags') as string[] | null | undefined)"
                :initial-tags="(contextProps.item?.__crud_tags as any[]) ?? []"
                :allow-management="canManageTags"
                @update:model-value="(value) => editor.setFieldValue('__crud_tags', value)"
            />

            <!-- Footer - only show for regular tabs (not child tabs or danger zone) -->
            <Teleport v-if="isMounted && !isChildTab(currentTab) && !isDangerZoneTab" to="#footer-portal">
                <div class="flex flex-1 items-center justify-end">
                    <div v-show="combinedIsDirty" class="flex gap-2">
                        <Button type="button" variant="outline" @click="handleReset">
                            <RotateCcw class="mr-2 size-4" />
                            {{ t('crud.reset') }}
                        </Button>
                        <Button type="submit" form="crud-editor-form" :disabled="editor.isProcessing.value">
                            <Save class="mr-2 size-4" />
                            {{ t('crud.save') }}
                        </Button>
                    </div>
                </div>
            </Teleport>
        </form>

        <!-- Conflict Detection Dialog -->
        <ConflictDialog
            :open="hasConflict"
            :conflict="conflictInfo"
            :translation-prefix="activeConfig?.translationPrefix"
            @update:open="(val) => { if (!val) dismissConflict(); }"
            @keep-mine="dismissConflict"
            @accept-theirs="handleAcceptServerChanges"
            @refresh="router.reload()"
        />
    </div>
</template>
